import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest, getApiUrl } from '../authConfig';
import DataPageHeader from '../components/Common/DataPageHeader';
import EmailDraftModal from '../components/Common/EmailDraftModal';
import Loader from '../components/Common/Loader';
import { getValidToken } from '../utils/msalTokenUtil';
import { FaInbox, FaSignature, FaRegFileAlt, FaLink, FaFolderOpen, FaTimes, FaChevronRight } from 'react-icons/fa';

const ESignRequestPage = () => {
  const location = useLocation();
  const state = location.state || {};

  // Controlled form states
  const [customerId] = useState(state.customerId || '');
  const [taxType, setTaxType] = useState(state.taxType || 'Personal');
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [onedriveId, setOnedriveId] = useState('');
  const [onedriveDriveId, setOnedriveDriveId] = useState(null);
  const [fileName, setFileName] = useState('');
  const [clientEmail, setClientEmail] = useState(state.clientEmail || '');
  const [clientName, setClientName] = useState(state.clientName || '');
  
  const [loading, setLoading] = useState(false);
  const [emailModalConfig, setEmailModalConfig] = useState(null);

  // MSAL & OneDrive state
  const [account, setAccount] = useState(null);
  const [pastedLink, setPastedLink] = useState('');
  const [isFetchingLink, setIsFetchingLink] = useState(false);

  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);

  useEffect(() => {
    let isMounted = true;
    const initializeMsal = async () => {
      try {
        if (typeof msalInstance.initialize === 'function') {
          await msalInstance.initialize();
        }
        const response = await msalInstance.handleRedirectPromise();
        if (isMounted) {
          if (response && response.account) {
            setAccount(response.account);
          } else {
            const currentAccounts = msalInstance.getAllAccounts();
            if (currentAccounts.length > 0) {
              setAccount(currentAccounts[0]);
            }
          }
        }
      } catch (err) {
        console.error('MSAL Initialization Error:', err);
      }
    };
    initializeMsal();
    return () => { isMounted = false; };
  }, [msalInstance]);

  // --- Dynamic Breadcrumb Logic ---
  const getBackLink = () => {
    // Update these paths to match your actual routing structure
    if (taxType === 'CVITP') return '/cvitp';
    return '/personalTax'; 
  };

  const getBackLabel = () => {
    if (taxType === 'CVITP') return 'CVITP Dashboard';
    return 'Personal Tax Dashboard';
  };
  // --------------------------------

  const handleFetchPastedLink = async () => {
    if (!account) {
      toast.warn('Please sign in first (via the header) to fetch files.');
      return;
    }
    
    let cleanLink = pastedLink.trim();
    if (!cleanLink) {
      toast.warn("Please paste a valid OneDrive or SharePoint link.");
      return;
    }
    
    if (!cleanLink.startsWith('http://') && !cleanLink.startsWith('https://')) {
      toast.warn("Please paste the full URL including https://");
      return;
    }

    setIsFetchingLink(true);
    try {
      try { new URL(cleanLink); } catch (e) { throw new Error("The pasted text does not appear to be a valid URL format."); }

      const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: ['Files.ReadWrite.All'], account });
      const base64Value = btoa(unescape(encodeURIComponent(cleanLink)))
        .replace(/\//g, '_')
        .replace(/\+/g, '-')
        .replace(/=+$/, '');
      const encodedUrl = `u!${base64Value}`;

      const response = await fetch(`https://graph.microsoft.com/v1.0/shares/${encodedUrl}/driveItem`, {
        headers: { 'Authorization': `Bearer ${tokenResponse.accessToken}` }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.error?.innerError?.code === "badArgument" || errData.error?.code === "invalidRequest") {
          throw new Error("Microsoft Graph could not recognize this as a valid sharing link. Please ensure you copied the entire URL correctly from OneDrive.");
        }
        throw new Error(errData.error?.message || "Could not access the file. Ensure the link is valid and you have permissions.");
      }

      const driveItem = await response.json();
      
      if (driveItem.folder) {
         throw new Error("The provided link points to a folder. Please provide a link to a specific file.");
      }

      setOnedriveId(driveItem.id);
      setOnedriveDriveId(driveItem.parentReference?.driveId || null);
      setFileName(driveItem.name);
      setPastedLink('');
    } catch (err) {
      console.error("Fetch link error:", err);
      toast.error(err.message);
    } finally {
      setIsFetchingLink(false);
    }
  };

  const handleLocalFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!account) {
      toast.warn('Please sign in first (via the header).');
      e.target.value = '';
      return;
    }

    setIsFetchingLink(true);
    setOnedriveId('');
    setOnedriveDriveId(null);
    setFileName('');

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({ scopes: ['Files.ReadWrite.All'], account });
      
      let matchedFiles = [];
      
      const searchPayload = {
        requests: [{
          entityTypes: ['driveItem'],
          query: { queryString: `filename:"${file.name.replace(/"/g, '')}"` }
        }]
      };

      const globalSearchResponse = await fetch('https://graph.microsoft.com/v1.0/search/query', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${tokenResponse.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(searchPayload)
      });

      if (globalSearchResponse.ok) {
        const globalData = await globalSearchResponse.json();
        const hits = globalData.value?.[0]?.hitsContainers?.[0]?.hits || [];
        
        matchedFiles = hits
          .map(hit => hit.resource)
          .filter(item => item && item.name === file.name && !item.folder);
      }

      if (matchedFiles.length === 0) {
         toast.error("Could not find this file in your OneDrive or Shared folders. Ensure it's fully synced.");
         return;
      }

      const selectedItem = matchedFiles[0];

      setOnedriveId(selectedItem.id);
      setOnedriveDriveId(selectedItem.parentReference?.driveId || null);
      setFileName(selectedItem.name);

    } catch (err) {
      console.error(err);
      toast.error('Could not match local file to OneDrive. ' + err.message);
    } finally {
      setIsFetchingLink(false);
      e.target.value = ''; 
    }
  };

  const handleLogin = async () => {
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) { toast.error(err.message || 'Microsoft sign-in failed.'); }
  };

  const handleLogout = async () => {
    try {
      await msalInstance.logoutRedirect({ account });
    } catch (err) { toast.error(err.message || 'Logout failed.'); }
  };

  const handleRegisterDocument = async (e) => {
    e.preventDefault();
    if (!account) {
      toast.warn('Please sign in first (via the header) to generate a link.');
      return;
    }

    if (!onedriveId) {
      toast.warn('Please select a document from OneDrive first.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        customer_id: customerId,
        tax_type: taxType,
        tax_year: parseInt(taxYear),
        onedrive_item_id: onedriveId,
        onedrive_drive_id: onedriveDriveId,
        file_name: fileName,
        shared_link: null
      };

      const accessToken = await getValidToken(msalInstance, account);
      const response = await fetch(getApiUrl('/v1/staff/initialize-document'), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (response.ok) {
        setEmailModalConfig({
          action: 'requestEsign',
          taxType: taxType,
          customer: { email: clientEmail },
          customData: {
            generatedLink: result.link,
            taxYear,
            clientName,
            clientEmail
          }
        });
      } else {
        toast.error(`Error: ${result.error || result.message}`);
      }
    } catch (err) {
      console.error("Failed handling server handshake", err);
      toast.error(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 pb-5">
      <Loader variant="overlay" show={loading || isFetchingLink} text={isFetchingLink ? "Syncing with Graph..." : "Initializing Secure Session..."} />

      <DataPageHeader
        title="eSign Request & Draft Review"
        description="Generate secure access links for client document review and e-signature."
        account={account}
        onLogin={handleLogin}
        onLogout={handleLogout}
      />

      <div className="container mt-4">
        
        {/* Dynamic Breadcrumbs */}
        <div className="mx-auto mb-3 px-3" style={{ maxWidth: '850px' }}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 align-items-center">
              <li className="breadcrumb-item">
                <Link to={getBackLink()} className="text-decoration-none text-primary fw-medium d-flex align-items-center gap-1 transition-all hover-lift">
                  ← Back to {getBackLabel()}
                </Link>
              </li>
              <li className="text-muted mx-2">
                <FaChevronRight style={{ fontSize: '0.7rem' }} />
              </li>
              <li className="breadcrumb-item active text-secondary small text-uppercase tracking-wider fw-bold mt-1" aria-current="page">
                eSign Request
              </li>
            </ol>
          </nav>
        </div>

        <div className="card shadow-lg border-0 rounded-4 mx-auto overflow-hidden" style={{ maxWidth: '850px' }}>
          
          <div className="card-header bg-white pt-5 pb-4 px-4 px-md-5 border-0 text-center">
            <div className="d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary rounded-circle mb-3 shadow-sm" style={{ width: '64px', height: '64px' }}>
              <FaSignature className="fs-2" />
            </div>
            <h2 className="h3 fw-bold text-dark mb-2">
              {clientName ? `Draft Review: ${clientName}` : 'Secure eSign Request'}
            </h2>
            <p className="text-muted mb-0">Configure and send a secure access link for client review and signature.</p>
          </div>
          
          <div className="card-body p-4 p-md-5 bg-white pt-0">
            <form onSubmit={handleRegisterDocument}>
              
              <div className="d-flex align-items-center mb-4 pb-3 border-bottom mt-2">
                <span className="bg-primary text-white fw-bold rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '32px', height: '32px'}}>1</span>
                <h5 className="mb-0 text-dark fw-bold">Client Information</h5>
              </div>
              
              <div className="row g-4 mb-5">
                <div className="col-md-6">
                  <label className="form-label fw-bold text-secondary small text-uppercase" style={{ letterSpacing: '0.5px' }}>Client Name</label>
                  <input type="text" className="form-control form-control-lg bg-light border-0 shadow-sm" value={clientName} onChange={(e) => setClientName(e.target.value)} required placeholder="John Doe" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold text-secondary small text-uppercase" style={{ letterSpacing: '0.5px' }}>Client Email</label>
                  <input type="email" className="form-control form-control-lg bg-light border-0 shadow-sm" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required placeholder="john@example.com" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold text-secondary small text-uppercase" style={{ letterSpacing: '0.5px' }}>Tax Type</label>
                  <select className="form-select form-select-lg bg-light border-0 shadow-sm" value={taxType} onChange={(e) => setTaxType(e.target.value)}>
                    <option value="Personal">Personal Tax (Paid)</option>
                    <option value="CVITP">CVITP Tax (Volunteer)</option>
                    <option value="Corporate">Corporate Tax</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-bold text-secondary small text-uppercase" style={{ letterSpacing: '0.5px' }}>Tax Year</label>
                  <input type="number" className="form-control form-control-lg bg-light border-0 shadow-sm" value={taxYear} onChange={(e) => setTaxYear(e.target.value)} required />
                </div>
              </div>

              <div className="d-flex align-items-center mb-4 pb-3 border-bottom">
                <span className="bg-primary text-white fw-bold rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{width: '32px', height: '32px'}}>2</span>
                <h5 className="mb-0 text-dark fw-bold">Document Source</h5>
              </div>

              <div className="p-4 bg-light rounded-4 border border-light-subtle d-flex flex-column gap-3 mb-4 shadow-sm">
                  <label className={`btn btn-primary btn-lg fw-medium d-flex align-items-center justify-content-center gap-3 shadow-sm ${isFetchingLink ? 'disabled' : ''}`}>
                    <FaFolderOpen className="fs-5" />
                    <span>Browse Local OneDrive Folder</span>
                    <input 
                      type="file" 
                      className="d-none" 
                      onChange={handleLocalFileSelect}
                      disabled={isFetchingLink}
                    />
                  </label>
                  
                  <div className="d-flex align-items-center gap-3 text-muted my-2">
                    <hr className="flex-grow-1 m-0 opacity-25" />
                    <span className="small text-uppercase fw-bold text-secondary" style={{letterSpacing: '1px'}}>OR</span>
                    <hr className="flex-grow-1 m-0 opacity-25" />
                  </div>

                  <div className="input-group input-group-lg shadow-sm rounded-3">
                    <span className="input-group-text bg-white border-end-0 text-muted ps-3">
                      <FaLink />
                    </span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-2" 
                      placeholder="Paste SharePoint / OneDrive link..." 
                      value={pastedLink} 
                      onChange={(e) => setPastedLink(e.target.value)} 
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleFetchPastedLink();
                        }
                      }}
                    />
                    <button 
                      type="button" 
                      className="btn btn-dark px-4 fw-medium" 
                      onClick={handleFetchPastedLink}
                      disabled={!pastedLink.trim() || isFetchingLink}
                    >
                      Fetch Link
                    </button>
                  </div>
                  <div className="text-center mt-2 text-muted small d-flex align-items-center justify-content-center gap-1">
                    <span className="text-primary fw-bold">ℹ️</span> Ensure the local file is fully synced to the cloud before selecting.
                  </div>
              </div>

              <div className="mb-5 mt-4">
                   {fileName ? (
                      <div className="p-4 bg-primary-subtle border border-primary-subtle rounded-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 shadow-sm transition-all">
                         <div className="d-flex align-items-center gap-3">
                            <div className="bg-white text-primary p-3 rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{width: '60px', height: '60px'}}>
                              <FaRegFileAlt className="fs-3" />
                            </div>
                            <div>
                               <p className="mb-1 fw-bold text-dark fs-5">{fileName}</p>
                               <span className="badge bg-white text-secondary border font-monospace text-truncate d-inline-block shadow-sm" style={{ maxWidth: '300px' }}>
                                  ID: {onedriveId}
                               </span>
                            </div>
                         </div>
                         <button type="button" className="btn btn-outline-danger bg-white fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2" onClick={() => { setFileName(''); setOnedriveId(''); setOnedriveDriveId(null); }}>
                            <FaTimes /> Remove File
                         </button>
                      </div>
                   ) : (
                      <div className="p-5 bg-light rounded-4 text-center text-muted" style={{ border: '2px dashed #ced4da' }}>
                        <FaInbox className="fs-1 d-block mx-auto mb-3 text-secondary opacity-50" />
                        <h5 className="fw-bold text-dark mb-1">No Document Attached</h5>
                        <p className="small mb-0">Select a local synced file or paste a secure link above.</p>
                      </div>
                   )}

                {fileName && (
                   <div className="mt-4 p-4 bg-light border border-light-subtle rounded-4 shadow-sm">
                     <label className="form-label fw-bold text-dark mb-2">
                       Display Title <span className="text-muted fw-normal ms-1">(Visible to client in email)</span>
                     </label>
                     <input type="text" className="form-control form-control-lg bg-white border-0 px-3 py-2 shadow-sm" value={fileName} onChange={(e) => setFileName(e.target.value)} />
                   </div>
                )}
              </div>

              <div className="d-grid mt-5 pt-4 border-top">
                <button type="submit" disabled={loading || !onedriveId} className="btn btn-success btn-lg py-3 fs-5 fw-bold shadow-sm d-flex justify-content-center align-items-center gap-2 rounded-pill w-100 transition-all">
                  ✉️ Generate & Email Secure Draft
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      {emailModalConfig && (
        <EmailDraftModal 
          customerData={emailModalConfig.customer}
          action={emailModalConfig.action}
          taxType={emailModalConfig.taxType}
          customData={emailModalConfig.customData}
          msalInstance={msalInstance}
          account={account}
          onClose={() => setEmailModalConfig(null)}
        />
      )}
    </div>
  );
};

export default ESignRequestPage;